/*
 *  Campaign__Create_PostSelector.jsx
 *
 *
 *
 */
//==============================================================================
// External dep.
//==============================================================================
import React, { useState, useCallback } from "react";
import styled from "styled-components";
//------------------------------------------------------------------------------
// Internal dep.
//------------------------------------------------------------------------------
import ModalYesNo from "../../Modal__YesNo.jsx";
import { t } from "i18next";
//------------------------------------------------------------------------------
// Styled Components
//------------------------------------------------------------------------------
const PostSelectorWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 20px;
`;
const PostRemove = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  opacity: 0;
  transition: all 0.2s;
  cursor: pointer;
  width: 100%;
  background: #e8e8eb;
  line-height: 1;
  padding: 2px 0;
  &:hover {
    opacity: 0.9 !important;
    padding: 6px 0;
    background: #d4d4d7;
    color: #1e1f46;
  }
`;
const Post = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 4px;
  position: relative;
  min-height: 64px;
  min-width: 56px;
  height: 100%;
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s;
  border: 2px solid #e8e8eb;
  color: #1e1f46;
  font-family: Rubik;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0px;
  text-align: center;
  margin: 0 6px 6px 6px;
  padding: 12px 8px;
  overflow: hidden;
  &:first-child {
    margin-left: 0;
  }
  &:last-child {
    margin-right: 0;
  }
  &:hover {
    ${PostRemove} {
      opacity: 0.4;
    }
  }
  ${(props) =>
    props.active
      ? `
    border: 2px solid #1E22FF;
    background: #f6f6ff;
    box-shadow: 0 0 0 2px #e7e6fd;
  `
      : `
    &:hover {
      border: 2px solid #D4D4D7;
      box-shadow: 0 0 0 2px #E8E8EB;
    };
  `}
`;
//------------------------------------------------------------------------------
// Main Functional Component
//------------------------------------------------------------------------------
const CampaignCreatePostSelector = ({
  channelPosts,
  addPost,
  removePost,
  selectedPostNumber,
  setSelectedPostNumber,
  selectedPreviewChannel,
  allowMultiplePostsPerChannel,
  maxPostPerChannel,
  editable
}) => {
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [postToRemove, setPostToRemove] = useState(null);

  const openRemoveModal = useCallback(({ postId, postNumber }) => {
    setPostToRemove({ postId, postNumber });
    setShowRemoveModal(true);
  }, []);

  const confirmRemove = useCallback(() => {
    removePost(postToRemove);
    setShowRemoveModal(false);
  }, [removePost, postToRemove]);

  const rejectRemove = useCallback(() => {
    setPostToRemove(null);
    setShowRemoveModal(false);
  }, []);

  return (
    <PostSelectorWrapper>
      <ModalYesNo
        yesClicks={confirmRemove}
        noClick={rejectRemove}
        show={showRemoveModal}
        headline={t("Confirm removal")}
        text={t("Do you really want to remove this post permanently?")}
      />
      {(channelPosts || [])
        .sort((a, b) => a.postNumber - b.postNumber)
        .map(({ postNumber, id }) => (
          <Post
            title={"Post: " + postNumber + ": " + selectedPreviewChannel}
            key={postNumber}
            onClick={() => setSelectedPostNumber(postNumber)}
            active={postNumber === selectedPostNumber}
          >
            <span>✍🏼</span>
            <span>
              {t("POST")} {postNumber}
            </span>
            {editable && channelPosts.length > 1 && (
              <PostRemove
                onClick={(e) => {
                  e.stopPropagation();
                  openRemoveModal({ postId: id, postNumber });
                }}
              >
                {t("REMOVE")}
              </PostRemove>
            )}
          </Post>
        ))}
      {editable &&
        allowMultiplePostsPerChannel &&
        maxPostPerChannel &&
        (channelPosts || []).length < maxPostPerChannel &&
        selectedPreviewChannel !== "linkedin" &&
        selectedPreviewChannel !== "indeed" && (
          <>
            <Post onClick={addPost}>
              <span>✍🏼</span>
              <img src="/images/add.svg" alt="" />
            </Post>
          </>
        )}
    </PostSelectorWrapper>
  );
};

export default CampaignCreatePostSelector;
